import { useState, useEffect, useMemo } from "react";
import { Trash, Pencil, X, Bookmark, GripVertical, Pin } from "lucide-react";
import {
  DndContext, closestCenter,
} from "@dnd-kit/core";

import {
  SortableContext, useSortable, arrayMove, rectSortingStrategy,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

import { useUpdateProjectName, useDeleteProject, useAddSubProject, useUpdatePermission, useGetPermissions } from "../Use-auth";
import Input from "./Input";
import ProjectsPage from "../ui/ProjectsPage";
import { api } from "../../lib/api";
import Button from "./Button";
import { getStatusLabelColor } from "../Pages/utils/taskUtils";
import { useLocation } from "wouter";

function SortableProject({ project, children, currentUser }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: project.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const canReorder = ["Admin", "Management"].includes(
    currentUser?.department
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="relative group"
    >
      {canReorder && (
        <div
          {...listeners}
          className="absolute top-0 left-0 z-10 cursor-grab active:cursor-grabbing 
             bg-[#d4d4d4] 
             hover:bg-black
             rounded-[0.5rem_0_0.5rem_0] 
             p-[2px] transition-all duration-200"
        >
          <GripVertical className="w-3 h-3 min-[541px]:w-4 min-[541px]:h-4 
                          text-gray-500 hover:text-white" />
        </div>
      )}
      {children}
    </div>
  );
}

const ProjectList = ({
  projects = [],
  activeProjectId,
  setActiveProjectId,
  currentUser,
  projectIds,
  TaskEmployees,
  employees = [],
  activeSubProjectId,
  setActiveSubProjectId,
  totalStaff = [],
  selectedProjectData,
  autoClickIdSubProject,
  setAutoClickIdSubProject,
  setOrderedProjects,
  orderedProjects,
  selectProjectFilter,
  setSelectProjectFilter,
  setSearchTerm,
  searchTerm,
  statuses
}) => {

  const [editingProject, setEditingProject] = useState(null);
  const [newProjectName, setNewProjectName] = useState("");
  const [isMultiProject, setIsMultiProject] = useState(false);
  const [sharedWith, setSharedWith] = useState([]);
  const [newSubProjectName, setNewSubProjectName] = useState("");
  const [subProjects, setSubProjects] = useState([]);
  const [activeSubProject, setActiveSubProject] = useState(null);
  const [status, setStatus] = useState("Not started");
  const updatePermission = useUpdatePermission();
  const { data: permissionsApi = [], refetch } = useGetPermissions();
  const updateProjectNameMutation = useUpdateProjectName();
  const addSubProjectMutation = useAddSubProject();
  const deleteProjectMutation = useDeleteProject();
  const isEmployee = ["Development", "Sales", "Design"].includes(currentUser.department);
  const [isUpdatingProject, setIsUpdatingProject] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState(null);
  const [addingSubProject, setAddingSubProject] = useState(false);
  const [subStatus, setSubStatus] = useState("Not started");
  const [isPinned, setIsPinned] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const [location] = useLocation();

  useEffect(() => {
    const handlePopState = () => {
      setSelectProjectFilter(new URLSearchParams(window.location.search).get("filter") || "");
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const matchedStaff = totalStaff
    ?.flatMap(dept => dept.employees || [])
    .find(emp => emp.employeeId === currentUser.userId);

  const safeSearch = (searchTerm || "").toLowerCase().trim();

  const statusPriority = {
    "In Progress": 1,
    "Not started": 2,
    "On Hold": 3,
    "Over Due": 4,
    "Completed": 5,
    "Lost": 6,
  };

  const filteredProjects = useMemo(() => {
    return projects
      .filter(project => {
        if (!selectProjectFilter) return true;
        return project.status === selectProjectFilter;
      })
      .filter(project => {
        if (!isEmployee) return true;

        const idsArray = Array.isArray(projectIds) ? projectIds : [projectIds];
        return idsArray
          .map(id => id.toString())
          .includes(project.id.toString());
      })
      .filter(project =>
        (project.projectName || "")
          .toLowerCase()
          .includes(safeSearch)
      )
      .sort((a, b) => {
        const pinA = Number(a.isPinned) || 0;
        const pinB = Number(b.isPinned) || 0;

        if (pinA !== pinB) {
          return pinB - pinA;
        }
        const priorityA = statusPriority[a.status] ?? 999;
        const priorityB = statusPriority[b.status] ?? 999;

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        const orderA = a.order ?? 0;
        const orderB = b.order ?? 0;

        if (orderA !== orderB) {
          return orderA - orderB;
        }

        return new Date(b.updatedAt) - new Date(a.updatedAt);
      });
  }, [projects, selectProjectFilter, projectIds, safeSearch, isEmployee]);

  useEffect(() => {
    setOrderedProjects(filteredProjects);
  }, [filteredProjects]);

  const handleRenameClick = (project) => {
    setEditingProject(project);
    setNewProjectName(project.projectName || "");
    setIsMultiProject(project.isMultiProject || false);
    setSharedWith(project.sharedWith || []);
    setStatus(project.status || "Not started");
    setIsPinned(Number(project.isPinned) === 1);
  };

  const permissionByUserId = {};
  permissionsApi.forEach(p => {
    permissionByUserId[p.userId] = p;
  });

  const handleUpdateSubmit = () => {
    if (!newProjectName.trim() || !editingProject) return;

    setIsUpdatingProject(true);

    const finalStatus = status || "Not started";

    const sharedWithEmployeeIds = sharedWith.map(emp =>
      typeof emp === "string" ? emp : emp.employeeId
    );

    let finalSharedWith = [...sharedWith];

    if (
      matchedStaff?.employeeId &&
      !finalSharedWith.includes(matchedStaff.employeeId)
    ) {
      finalSharedWith.push(matchedStaff.employeeId);
    }

    updateProjectNameMutation.mutate(
      {
        projectId: editingProject.id,
        projectName: newProjectName,
        isMultiProject: editingProject?.isMultiProject
          ? true
          : isMultiProject,
        sharedWith: finalSharedWith,
        status: finalStatus,
        isPinned,
        updatedBy: {
          userId: matchedStaff?.employeeId,
          fullName: currentUser?.fullName || "Unknown",
          email: matchedStaff?.officeEmail,
        },
      },
      {
        onSuccess: (res) => {
          setOrderedProjects((prev) =>
            prev.map((p) =>
              p.id === editingProject.id
                ? {
                  ...p,
                  projectName: newProjectName,
                  status: status,
                  isPinned: isPinned ? 1 : 0,
                }
                : p
            )
          );
          setEditingProject(null);
          setIsUpdatingProject(false);
          employees.forEach(emp => {
            const permission = permissionByUserId[emp.employeeId];
            if (!permission) {
              console.warn("No permission for employee:", emp.employeeId);
              return;
            }

            const rawProjectIds = permission.employees?.project_id;

            const existingProjects = Array.isArray(rawProjectIds)
              ? rawProjectIds.map(id => id.toString())
              : [];

            const projectIdStr = editingProject._id.toString();

            const hasProject = existingProjects.includes(projectIdStr);
            const shouldHaveProject = sharedWithEmployeeIds.includes(emp.employeeId);

            if (shouldHaveProject && !hasProject) {
              updatePermission.mutate({
                id: permission._id,
                employees: {
                  ...permission.employees,
                  project_show: true,
                  project_id: [...existingProjects, projectIdStr],
                },
                management: permission.management || {},
              });
            }
            if (!shouldHaveProject && hasProject) {
              updatePermission.mutate({
                id: permission._id,
                employees: {
                  ...permission.employees,
                  project_id: existingProjects.filter(
                    id => id !== projectIdStr
                  ),
                },
                management: permission.management || {},
              });
            }
          });
          setEditingProject(null);
          setIsUpdatingProject(false);
        },
        onError: (err) => {
          console.error("Project update failed:", err);
          setIsUpdatingProject(false);
        },
      }
    );
  };

  const handleDelete = (projectId) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;

    setDeletingProjectId(projectId);

    deleteProjectMutation.mutate(
      { projectId },
      {
        onSuccess: () => {
          setOrderedProjects((prev) => prev.filter(p => p.id !== projectId));
          setDeletingProjectId(null);
        },
        onError: (err) => {
          console.error("Delete project failed:", err);
          setDeletingProjectId(null);
        },
      }
    );
  };

  const normalizeId = (id) => String(id);

  const activeProject = filteredProjects.find(
    p => normalizeId(p.id) === normalizeId(activeProjectId)
  );

  const handleAddSubProject = () => {

    const trimmedName = newSubProjectName.trim();
    if (!trimmedName || !activeProjectId) return;

    const allSubNames = [
      ...(activeProject?.subProjects || []),
      ...subProjects
    ].map(sub => sub.projectName.toLowerCase());

    if (allSubNames.includes(trimmedName.toLowerCase())) {
      alert(`Sub-project "${trimmedName}" already exists!`);
      return;
    }

    setAddingSubProject(true);

    const newSub = {
      projectName: trimmedName,
      createdBy: currentUser.username,
      subStatus
    };

    addSubProjectMutation.mutate(
      {
        projectId: activeProjectId,
        subProject: newSub,
      },
      {
        onSuccess: (data) => {

          const newSubProject = {
            ...data.data,
            _id: data.data.id
          };

          setSubProjects(prev => [...prev, newSubProject]);

          setNewSubProjectName("");
          setSubStatus("Not started");
          setAddingSubProject(false);
         setShowAddModal(false)
        },
        onError: (err) => {
          console.error("Failed to add sub-project:", err);
          setAddingSubProject(false);
        },
      }
    );
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setOrderedProjects((items) => {
      const oldIndex = items.findIndex((p) => p.id === active.id);
      const newIndex = items.findIndex((p) => p.id === over.id);

      let newItems = arrayMove(items, oldIndex, newIndex);

      const pinned = newItems.filter((p) => Number(p.isPinned) === 1);
      const unpinned = newItems.filter((p) => Number(p.isPinned) !== 1);

      newItems = [...pinned, ...unpinned];

      localStorage.setItem(
        "projectOrder",
        JSON.stringify(newItems.map((p) => p.id))
      );

      const projectOrder = newItems.map((p, idx) => ({
        id: p.id,
        order: idx + 1,
      }));

      fetch(api.auth.projects.order.path, {
        method: api.auth.projects.order.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectOrder }),
        credentials: "include",
      }).catch((err) => console.error("Order update failed", err));

      return newItems;
    });
  };

  const canReorder = ["Admin", "Management"].includes(
    currentUser?.department
  );

  if (!currentUser) return null;

  return (
    <>
      <div>
        {!canReorder && !activeProjectId && !activeSubProjectId && (<div className="flex gap-2 my-5 flex-row sm:items-center sm:justify-end sm:max-w-md sm:ml-auto">
          <select
            name="projectFilter"
            id="projectFilter"
            className="input text-sm max-[540px]:text-xs flex-1 min-w-[150px] sm:min-w-[150px] sm:max-w-[200px] px-3 py-2 max-[540px]:px-2 max-[540px]:py-2"
            value={selectProjectFilter}
            onChange={(e) => setSelectProjectFilter(e.target.value)}
          >
            <option value="">All Projects</option>
            <option value="Not started">Not started</option>
            <option value="In Progress">Active projects</option>
            <option value="Over Due">Over Due Projects</option>
            <option value="On Hold">On Hold projects</option>
            <option value="Lost">Lost Projects</option>
            <option value="Completed">Completed Projects</option>
          </select>

          <Input
            type="search"
            placeholder="Search project..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-52"
          />
        </div>)}

        {!activeProjectId && !activeSubProjectId && (
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedProjects.map((p) => p.id)}
              strategy={rectSortingStrategy}
            >
              {/* GRID */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">

                {orderedProjects.map((project) => (
                  <SortableProject
                    key={project.id}
                    project={project}
                    currentUser={currentUser}
                  >
                    <div
                      className="relative group p-4 xs:p-5 rounded-md xs:rounded-xl cursor-pointer
              bg-white border border-gray-200 shadow-sm
              hover:shadow-lg "
                      onClick={() => setActiveProjectId(project.id)}
                    >

                      {!isEmployee && (
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                          <Trash
                            size={25}
                            className={`p-1 rounded cursor-pointer hover:bg-red-100
                    ${deletingProjectId === project.id
                                ? "text-gray-400"
                                : "text-red-500"
                              }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (deletingProjectId !== project.id)
                                handleDelete(project.id);
                            }}
                          />

                          <Pencil
                            size={25}
                            className="p-1 rounded text-blue-500 cursor-pointer hover:bg-blue-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRenameClick(project);
                            }}
                          />
                        </div>
                      )}

                      {/* NAME */}
                      <div className="font-semibold text-[14px] xs:text-base  text-gray-800 capitalize truncate">
                        {project.projectName}
                      </div>

                      {/* TYPE */}
                      <div className="text-xs mt-1 text-gray-500">
                        {project.isMultiProject
                          ? "Multi Project"
                          : "Single Project"}
                      </div>

                      {/* STATUS */}
                      <div
                        className={`absolute bottom-0 right-0 flex items-center gap-1
    text-[11px] px-2 py-0.5 font-medium
    rounded-[0.5rem_0_0.5rem_0]
    ${getStatusLabelColor(project.status)}`}
                      >
                        <Bookmark size={12} />
                        {project.status || "Pending"}
                      </div>
                      {project.isPinned === 1 && (
                        <Pin className="absolute top-0 right-0 w-4 h-4 text-yellow-500" />
                      )}

                      {deletingProjectId === project.id && (
                        <span className="absolute bottom-2 left-2 text-xs text-gray-400 animate-pulse">
                          Deleting...
                        </span>
                      )}

                    </div>
                  </SortableProject>
                ))}

              </div>
            </SortableContext>
          </DndContext>
        )}
        <div>
          {activeProject?.isMultiProject && activeProjectId ? (
            <ProjectsPage
              activeProject={activeProject}
              newSubProjectName={newSubProjectName}
              setNewSubProjectName={setNewSubProjectName}
              handleAddSubProject={handleAddSubProject}
              setActiveProjectId={setActiveProjectId}
              subProjects={subProjects}
              activeSubProject={activeSubProject}
              setActiveSubProject={setActiveSubProject}
              activeSubProjectId={activeSubProjectId}
              setActiveSubProjectId={setActiveSubProjectId}
              isEmployee={isEmployee}
              autoClickIdSubProject={autoClickIdSubProject}
              setAutoClickIdSubProject={setAutoClickIdSubProject}
              addingSubProject={addingSubProject}
              subStatus={subStatus}
              setSubStatus={setSubStatus}
              setSubProjects={setSubProjects}
              currentUser={currentUser}
              projects={projects}
              activeProjectId={activeProjectId}
              statuses={statuses}
              employees={employees}
              showAddModal={showAddModal}
              setShowAddModal={setShowAddModal}
            />
          ) : (<div>
          </div>)}
        </div>

        {/* EDIT MODAL */}
        {editingProject && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] p-2 max-[540px]:p-1"
            onClick={() => setEditingProject(null)}>
            <div className="
        bg-white rounded-xl shadow-2xl
        w-full max-w-2xl
        max-[540px]:max-w-[95%]
        max-h-[90vh]
        p-6 max-[540px]:p-3
        relative animate-scaleIn
        "

              onClick={(e) => e.stopPropagation()}>
              <div
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 cursor-pointer"
                onClick={() => setEditingProject(null)}
              >
                <X size={20} />
              </div>

              <h3 className="text-xl max-[540px]:text-base font-semibold mb-5 max-[540px]:mb-3 text-center">
                Edit Project</h3>

              <div className="mb-4">
                <Input
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Project Name"
                />
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4 max-[540px]:mb-3">
                <Button
                  active={!isMultiProject}
                  onClick={() => {
                    if (editingProject?.isMultiProject) {
                      alert("This project cannot be converted from multi to single");
                      return;
                    }
                    setIsMultiProject(false);
                  }}
                >
                  Single Project
                </Button>

                <Button
                  active={isMultiProject}
                  onClick={() => setIsMultiProject(true)}
                >
                  Multi Project
                </Button>
              </div>

              <div className="mb-4">
                <label className="block mb-1 text-sm max-[540px]:text-xs font-medium text-gray-700">
                  Status
                </label>
                <div>
                  <select name="projectStatus" id="projectStatus"
                    className="
          w-full border border-gray-300 rounded-lg
          px-3 py-2 
          text-sm max-[540px]:text-xs
          focus:outline-none focus:ring-2 focus:ring-blue-400
        "
                    value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="">Select Status</option>
                    <option value="Not started">Not started</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Over Due">Over Due</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Lost">Lost</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
              </div>
              <div className="mb-4 flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  Pin Project
                </label>

                <input
                  type="checkbox"
                  checked={isPinned}
                  onChange={(e) => setIsPinned(e.target.checked)}
                  className="w-4 h-4 cursor-pointer"
                />
              </div>
              <div className="mb-4 flex justify-between items-center">
                <label className="block mb-1 text-sm max-[540px]:text-xs font-medium text-gray-700">
                  Share With
                </label>
                <div className="flex gap-2">
                  <TaskEmployees
                    selected={sharedWith}
                    onChange={setSharedWith}
                    employees={employees}
                    placement="top"
                    align="right"
                  />
                </div>
              </div>

              <Button
                className='float-right'
                variant="success"
                onClick={!isUpdatingProject ? handleUpdateSubmit : undefined}
                disabled={isUpdatingProject}
              >
                {isUpdatingProject ? (
                  <>
                    Saving...
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default ProjectList;